import React, { useState } from "react";
import axios from "axios";
import ReCAPTCHA from "react-google-recaptcha";
import {
  TextField,
  Button,
  Typography,
  Box,
  Snackbar,
  Alert,
  Stack,
  Divider
} from "@mui/material";
import { useNavigate, Link } from "react-router-dom";
import { Notyf } from 'notyf';
import { GoogleLogin } from '@react-oauth/google';


const notyf = new Notyf({
  position: { x: 'center', y: 'top' },
  duration: 3000,
});

const generateOrderId = () => 'ORDER_' + Date.now();

const generateSubscriptionId = () => 'SUB_' + Math.random().toString(36).substr(2, 9).toUpperCase();


const Register = ({ setToken }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [cpassword, setCPassword] = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const handleCaptchaChange = (value) => {
    setCaptchaToken(value);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (password.length <= 6) {
      //handleSnackbar("Password must be greater than 6 characters!", "error");
      notyf.error('Password must be greater than 6 characters!');
      setLoading(false);
      return;
    }

    if (password !== cpassword) {
      //handleSnackbar("Passwords do not match!", "error");
      notyf.error('Your passwords do not match!');
      setLoading(false);
      return;
    }

    if (!captchaToken) {
      //handleSnackbar("Please complete the reCAPTCHA!", "error");
      notyf.error('Please complete the reCAPTCHA!');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post("https://admin.bizstor.lk/public/api/register", {
        name,
        email,
        password,
        status: "active",
        "g-recaptcha-response": captchaToken,
      });

      if (response.status === 201 || response.data.success) {
        //handleSnackbar("Registered successfully!", "success");
        notyf.success('Registered successfully!');

        const orderId = generateOrderId();
        const subscriptionId = generateSubscriptionId();

        const currentPaymentData = {
          user_email: email,
          subscription_id: subscriptionId,
          amount: 0,
          user_phone: "",
          first_name: name,
          last_name: name
        };

        const selectedPlan = "30 days free";
        const billingCycle = "monthly";

        await saveSubscriptionToDatabase(orderId, currentPaymentData, selectedPlan, billingCycle);

        setLoading(false);
        navigate("/login");
      } else {
        throw new Error("Registration failed");
      }
    } catch (error) {
      //handleSnackbar("Registration failed!", "error");
      notyf.error('Registration failed!');
      setLoading(false);
    }
  };

  const saveSubscriptionToDatabase = async (orderId, currentPaymentData, selectedPlan, billingCycle) => {
    try {
      const response = await fetch('https://admin.bizstor.lk/public/api/save-subscription', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          email: currentPaymentData.user_email,
          plan: selectedPlan,
          billing_cycle: billingCycle,
          subscription_id: currentPaymentData.subscription_id,
          amount: currentPaymentData.amount,
          order_id: orderId,
          payment_status: 'completed',
          user_phone: currentPaymentData.user_phone,
          first_name: currentPaymentData.first_name,
          last_name: currentPaymentData.last_name
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save subscription data');
      }

      //console.log('Subscription data saved successfully:', data);
    } catch (error) {
      console.error('Failed to save subscription:', error);
      //alert('There was an issue saving your subscription. Please contact support with your order ID: ' + orderId);
    }
  };


  {/*google login */ }
  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);

    if (!credentialResponse?.credential) {
      handleSnackbar("Google Credential Missing", "error");
      setLoading(false);
      return;
    }

    try {
      // Send Google Token to backend for verification and registration
      const response = await axios.post("https://admin.bizstor.lk/public/api/google-register", {
        token: credentialResponse.credential,
      });

      const { user, authToken, isNewUser } = response.data;

      localStorage.setItem("token", authToken);
      setToken(authToken);

      if (isNewUser) {
        // Generate subscription info
        const orderId = generateOrderId();
        const subscriptionId = generateSubscriptionId();

        const currentPaymentData = {
          user_email: user.email,
          subscription_id: subscriptionId,
          amount: 0,
          user_phone: user.phone || "",
          first_name: user.name || "",
          last_name: user.name || "",
        };

        const selectedPlan = "30 days free";
        const billingCycle = "monthly";

        await saveSubscriptionToDatabase(orderId, currentPaymentData, selectedPlan, billingCycle);
      }

      notyf.success('Login successfully!');
      setLoading(false);
      navigate("/dashboard");

    } catch (error) {
      notyf.error('Login failed!');
      setLoading(false);
    }
  };


  return (
    <Box
      sx={{
        minHeight: "95vh",
        width: "100vw",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          p: 4,
          //boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          //bgcolor: "white",
          maxWidth: "400px",
          width: "100%",
        }}
      >
        <Typography component="h1" variant="h5" fontWeight={600}>
          Create an Account
        </Typography>
        <Box component="form" onSubmit={handleRegister} sx={{ mt: 1 }}>
          <TextField
            variant="standard"
            margin="normal"
            required
            fullWidth
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            variant="standard"
            margin="normal"
            required
            fullWidth
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            variant="standard"
            margin="normal"
            required
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <TextField
            variant="standard"
            margin="normal"
            required
            fullWidth
            label="Confirm Password"
            type="password"
            value={cpassword}
            onChange={(e) => setCPassword(e.target.value)}
          />

          {/* Google reCAPTCHA */}
          <Box sx={{ mt: 2, mb: 2, display: "flex", justifyContent: "center" }}>
            <ReCAPTCHA
              sitekey="6LcAfMwqAAAAAHhL5kE4nje6w0s_xBJR6j-w7spl"
              onChange={handleCaptchaChange}
            />
          </Box>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 3,
              mb: 2,
              borderRadius: 20,
              background: 'rgb(29, 29, 29)',
              "&:hover": { backgroundColor: "black" },
            }}
          >
            Create Account
          </Button>

          {/**<Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" sx={{ mb: 1, width: "100%" }}>
            <Stack direction="row" alignItems="center" spacing={1} width="100%" mb={1}>
              <Divider sx={{ flex: 1 }} />
              <Typography variant="body" sx={{ mx: 1, color: 'text.secondary' }}>
                OR
              </Typography>
              <Divider sx={{ flex: 1 }} />
            </Stack>
            <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => console.log("Login Failed")} />
          </Box>*/}

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Typography variant="body2" sx={{ mt: 2 }}>
              Already have an account?{" "}
              <Link to="/login" style={{ textDecoration: "none" }}>Login</Link>
            </Typography>
            <Typography variant="body2" mt={1}>
              Back to <Link to="/" style={{ textDecoration: 'none' }}>Home</Link>
            </Typography>
          </Box>
        </Box>
      </Box>

      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "white",
            zIndex: 9999,
          }}
        >
          <l-trio size="50" speed="1.3" color="red"></l-trio>
        </Box>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Register;
